import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { LineChartElement } from "./catalog"

import {
  ChartTitle,
  colorForSeries,
  CHART_OUTLINE,
  computeSeriesStats,
  DEFAULT_YAXIS_WIDTH,
  formatTickNumber,
  renderSeriesStatsText,
  sharedTooltipProps,
  TIMESERIES_SYNC_ID,
} from "./chart-shared"
import { TileEmptyState } from "../components/tile-empty-state"
import { useChartClickFilter } from "../hooks/use-chart-click-filter"

const LINE_WIDTH = 2.5

export function LineChartComponent({ element }: { element: LineChartElement }) {
  // Click on a data point writes the xKey value (typically a date or
  // category) into state. Recharts hands the datum back via the
  // dot/activeDot's onClick; we reuse the shared hook so the toggle-
  // off-on-second-click behaviour matches bar + pie.
  const handlePointClick = useChartClickFilter(element.onClickFilter, element.xKey)
  if (!Array.isArray(element.data) || element.data.length === 0) {
    return <TileEmptyState title={element.title} />
  }
  // Single-series line charts don't benefit from a legend — the title
  // names the series. Hide it for cleaner output.
  const showLegend = element.lines.length > 1
  // Single-series + non-dashed: render with an area gradient under the
  // stroke for a more substantial "filled" feel. Multi-series stays
  // line-only so overlapping fills don't stack into mud.
  const useAreaFill = element.lines.length === 1 && !element.lines[0]?.strokeDasharray

  // Per-series stats strip — Grafana's "last/avg/max/min" inline on
  // the legend. Always computed; shown below the chart when at least
  // one series produced finite readings. Compact one-row-per-series.
  const seriesStats = element.lines.map((line, i) => {
    const stats = computeSeriesStats(element.data, line.dataKey)
    return {
      key: line.dataKey,
      name: line.label ?? line.dataKey,
      color: colorForSeries(line.label ?? line.dataKey, i, line.color),
      stats,
    }
  })
  const showStatsStrip = seriesStats.some((s) => s.stats.last !== null)

  return (
    <div className="bg-card border-border rounded-xl border p-4 shadow-sm">
      <ChartTitle title={element.title} />
      <ResponsiveContainer height={280} width="100%">
        <ComposedChart
          data={element.data}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          syncId={TIMESERIES_SYNC_ID}
        >
          <defs>
            {element.lines.map((line, i) => {
              const c = colorForSeries(line.label ?? line.dataKey, i, line.color)
              return (
                <linearGradient
                  key={line.dataKey}
                  id={`line-area-${i}`}
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={c} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={c} stopOpacity={0.02} />
                </linearGradient>
              )
            })}
          </defs>
          <CartesianGrid className="stroke-border" strokeDasharray="3 3" vertical={false} />
          <XAxis axisLine={false} className="text-xs" dataKey={element.xKey} tickLine={false} />
          <YAxis
            axisLine={false}
            className="text-xs"
            tickFormatter={formatTickNumber}
            tickLine={false}
            width={DEFAULT_YAXIS_WIDTH}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--muted-foreground) / 0.3)", strokeWidth: 1 }}
            {...sharedTooltipProps}
          />
          {/* Recharts Legend hidden when our stats strip renders below — the strip
              already shows the colour bullet + series name + min/avg/max/last,
              so a duplicated Legend row would be visual noise. */}
          {showLegend && !showStatsStrip && (
            <Legend iconType="circle" wrapperStyle={{ paddingTop: 8 }} />
          )}
          {useAreaFill &&
            element.lines.map((line, i) => (
              <Area
                key={`fill-${line.dataKey}`}
                dataKey={line.dataKey}
                fill={`url(#line-area-${i})`}
                isAnimationActive={false}
                legendType="none"
                name={line.label ?? line.dataKey}
                stroke="none"
                type="monotone"
              />
            ))}
          {/* Underlays first so they render behind the coloured strokes: a foreground
              line two pixels wider keeps any series colour (however pale) at 3:1. They are
              excluded from legend and tooltip, so both keep one entry per series. */}
          {element.lines.map((line) => (
            <Line
              key={`${line.dataKey}-underlay`}
              activeDot={false}
              dataKey={line.dataKey}
              dot={false}
              isAnimationActive={false}
              legendType="none"
              stroke={CHART_OUTLINE}
              strokeDasharray={line.strokeDasharray}
              strokeWidth={LINE_WIDTH + 2}
              tooltipType="none"
              type="monotone"
            />
          ))}
          {element.lines.map((line, i) => (
            <Line
              key={line.dataKey}
              activeDot={
                handlePointClick
                  ? { r: 5, strokeWidth: 2, cursor: "pointer", onClick: handlePointClick }
                  : { r: 5, strokeWidth: 2 }
              }
              dataKey={line.dataKey}
              dot={false}
              name={line.label ?? line.dataKey}
              stroke={colorForSeries(line.label ?? line.dataKey, i, line.color)}
              strokeDasharray={line.strokeDasharray}
              strokeWidth={LINE_WIDTH}
              type="monotone"
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      {showStatsStrip && (
        <div className="text-muted-foreground mt-2 flex flex-col gap-0.5 text-[11px] tabular-nums">
          {seriesStats
            .filter((s) => s.stats.last !== null)
            .map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <span
                  className="border-foreground inline-block size-2 shrink-0 rounded-full border"
                  style={{ backgroundColor: s.color }}
                />
                <span className="text-foreground font-medium">{s.name}</span>
                <span className="opacity-80">{renderSeriesStatsText(s.stats)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

// eslint-disable-next-line import/no-default-export
export default LineChartComponent
